import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Plus, Search, Users, Building2, Phone, Mail, Calendar,
  MoreVertical, FileText, IndianRupee, ChevronRight, User,
  MapPin, Briefcase, Star
} from 'lucide-react';

const clients = [
  { id: 1, name: 'Sharma Industries Pvt Ltd', type: 'Corporate', contact: 'Rajesh Sharma', phone: '+91 98765 43210', email: 'rajesh@sharma.com', cases: 5, outstanding: 250000, status: 'active' },
  { id: 2, name: 'Verma & Sons', type: 'Partnership', contact: 'Amit Verma', phone: '+91 98765 43211', email: 'amit@verma.com', cases: 3, outstanding: 75000, status: 'active' },
  { id: 3, name: 'Gupta Holdings', type: 'Corporate', contact: 'Priya Gupta', phone: '+91 98765 43212', email: 'priya@gupta.com', cases: 8, outstanding: 0, status: 'active' },
  { id: 4, name: 'ABC Corporation', type: 'MNC', contact: 'John Smith', phone: '+91 98765 43213', email: 'john@abc.com', cases: 2, outstanding: 500000, status: 'vip' },
  { id: 5, name: 'Private Individual', type: 'Individual', contact: 'Self', phone: '+91 98765 43214', email: 'private@email.com', cases: 1, outstanding: 25000, status: 'active' },
];

const Clients = () => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  return (
    <MainLayout title="Clients" subtitle="Manage your client relationships">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Clients', value: '156', icon: Users, color: 'blue' },
          { label: 'Active Matters', value: '89', icon: Briefcase, color: 'green' },
          { label: 'Outstanding', value: '₹8.5L', icon: IndianRupee, color: 'yellow' },
          { label: 'VIP Clients', value: '12', icon: Star, color: 'purple' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="glass-card p-6 relative overflow-hidden"
          >
            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${stat.color}-500 to-${stat.color}-400`} />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <h3 className="text-3xl font-bold mt-1">{stat.value}</h3>
              </div>
              <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-4 py-3 w-80 rounded-xl input-glass"
          />
        </div>
        <motion.button
          className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-5 h-5" />
          <span>Add Client</span>
        </motion.button>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-3 gap-6">
        {clients.map((client, index) => (
          <motion.div
            key={client.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.02, y: -5 }}
            onClick={() => setSelectedClient(client)}
            className="glass-card p-6 cursor-pointer relative overflow-hidden group"
          >
            {/* VIP Badge */}
            {client.status === 'vip' && (
              <div className="absolute top-4 right-4">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              </div>
            )}

            {/* Hover Glow */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
            />

            <div className="relative z-10">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] flex items-center justify-center text-white text-xl font-bold">
                  {client.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{client.name}</h3>
                  <span className="text-sm text-gray-500">{client.type}</span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <User className="w-4 h-4" />
                  <span>{client.contact}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Phone className="w-4 h-4" />
                  <span>{client.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Mail className="w-4 h-4" />
                  <span>{client.email}</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-500/20">
                <div>
                  <p className="text-sm text-gray-500">Cases</p>
                  <p className="font-semibold">{client.cases}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Outstanding</p>
                  <p className={`font-semibold ${client.outstanding > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                    ₹{client.outstanding.toLocaleString()}
                  </p>
                </div>
                <motion.button
                  className="p-2 rounded-lg hover:bg-gray-500/10"
                  whileHover={{ scale: 1.1 }}
                >
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </MainLayout>
  );
};

export default Clients;
